import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import * as mutations from '../graphql/mutations';
import * as queries from '../graphql/queries';

const client = generateClient();

// Domain key mapping to convert between numerical indices and text descriptions
export const getRadiologyDomainKeyByIndex = (index) => {
  const domainKeys = [
    "Commitment to Specialty (double-weighted domain)",
    "Leadership and Management (minimum role duration 6 months)",
    "Teaching and Training",
    "Audit and Quality Improvement",
    "Academic Achievements (postgraduate achievements only for scoring)"
  ];
  
  // Convert to number and ensure it's handled as zero-based index
  const numericIndex = Number(index);
  console.log(`Converting Radiology domain index ${index} (type: ${typeof index}) to key, numeric value: ${numericIndex}`);
  
  if (isNaN(numericIndex)) {
    console.error(`Invalid Radiology domain index: ${index} is not a number`);
    return `Unknown domain ${index}`;
  }
  
  // Use zero-based indexing
  const result = domainKeys[numericIndex];
  console.log(`Radiology domain lookup result for index ${numericIndex}: ${result}`);
  
  // Return the domain name or "Unknown domain" if index is out of range
  return result || `Unknown domain ${index}`;
};

// Helper function to determine if a domain key is numeric
const isNumericDomainKey = (key) => {
  const result = !isNaN(Number(key)) && key !== null && key !== '';
  console.log(`Checking if Radiology domain key "${key}" is numeric: ${result}`);
  return result;
};

/**
 * Saves or updates progress for a Radiology domain
 * @param {string|number} domainKey - The key identifying the domain/category (can be number or string)
 * @param {number} progress - The progress value to save (number of completed tasks)
 * @returns {Promise} - The GraphQL mutation result
 */
export const saveRadiologyDomainProgress = async (domainKey, progress) => {
  try {
    console.log(`Radiology domain key before conversion: "${domainKey}" (type: ${typeof domainKey})`);
    
    // Convert numeric domainKey to text description
    const textDomainKey = isNumericDomainKey(domainKey) 
      ? getRadiologyDomainKeyByIndex(Number(domainKey))
      : domainKey;
    
    console.log(`Radiology domain key after conversion: "${textDomainKey}"`);
    
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      console.error('Authentication error: User not authenticated');
      throw new Error('User not authenticated');
    }
    console.log('User authenticated:', user.username);

    // Check if there's already a record for this domain
    console.log(`Checking for existing record with userId: ${user.username}, domainKey: ${textDomainKey}`);
    const { data } = await client.graphql({
      query: queries.listRadiologyDomainProgresses,
      variables: {
        filter: { 
          userId: { eq: user.username }, 
          domainKey: { eq: textDomainKey }
        }
      },
      authMode: 'userPool'
    });
    console.log('Query result:', JSON.stringify(data));

    const existingRecord = data.listRadiologyDomainProgresses.items[0];

    if (existingRecord) {
      // Update existing record
      console.log(`Updating existing record ${existingRecord.id} with progress: ${progress}`);
      const input = {
        id: existingRecord.id,
        userId: user.username,
        domainKey: textDomainKey, 
        progress,
        owner: user.username, // Make sure owner is explicitly set
        _version: existingRecord._version // Include version if using DataStore
      };

      console.log('Update input:', JSON.stringify(input));
      const result = await client.graphql({
        query: mutations.updateRadiologyDomainProgress,
        variables: { input },
        authMode: 'userPool'
      });

      console.log(`Updated Radiology domain progress for ${domainKey}:`, result);
      return result;
    } else {
      // Create new record
      console.log(`Creating new record for userId: ${user.username}, domainKey: ${textDomainKey}, progress: ${progress}`);
      const input = {
        userId: user.username,
        domainKey: textDomainKey, // Ensure domainKey is a string
        progress,
        owner: user.username // Make sure owner is explicitly set
      };

      console.log('Create input:', JSON.stringify(input));
      const result = await client.graphql({
        query: mutations.createRadiologyDomainProgress,
        variables: { input },
        authMode: 'userPool'
      });

      console.log(`Created Radiology domain progress for ${domainKey}:`, result);
      return result;
    }
  } catch (error) {
    console.error('Error saving Radiology domain progress:', error);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack,
      // For GraphQL errors, they may be nested
      errors: error.errors || 'No nested errors'
    });
    throw error;
  }
};

/**
 * Fetches progress for all Radiology domains for the current user
 * @returns {Promise<Array>} - Array of domain progress objects
 */
export const fetchRadiologyDomainsProgress = async () => {
  try {
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data } = await client.graphql({
      query: queries.listRadiologyDomainProgresses,
      variables: {
        filter: { userId: { eq: user.username } },
        limit: 100 // Ensure we get all records
      },
      authMode: 'userPool'
    });

    console.log('Fetched Radiology domain progress:', data.listRadiologyDomainProgresses.items);
    return data.listRadiologyDomainProgresses.items;
  } catch (error) {
    console.error('Error fetching Radiology domains progress:', error);
    return [];
  }
};

/**
 * Fetches progress for a specific Radiology domain
 * @param {string|number} domainKey - The key identifying the domain/category
 * @returns {Promise<Object>} - The domain progress object or null if not found
 */
export const fetchRadiologyDomainProgress = async (domainKey) => {
  try {
    console.log(`Fetching Radiology domain progress for domain key: "${domainKey}" (type: ${typeof domainKey})`);
    
    // Convert numeric domainKey to text description using the same logic as saveRadiologyDomainProgress
    const textDomainKey = isNumericDomainKey(domainKey) 
      ? getRadiologyDomainKeyByIndex(Number(domainKey))
      : domainKey;
    
    console.log(`Radiology domain key after conversion for fetch: "${textDomainKey}"`);
      
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      console.error('Authentication error: User not authenticated during fetch');
      throw new Error('User not authenticated');
    }
    console.log(`Fetching for user: ${user.username}, Radiology domain: ${textDomainKey}`);

    const { data } = await client.graphql({
      query: queries.listRadiologyDomainProgresses,
      variables: {
        filter: { 
          userId: { eq: user.username }, 
          domainKey: { eq: textDomainKey }
        }
      },
      authMode: 'userPool'
    });

    console.log(`Fetch result for Radiology domain ${textDomainKey}:`, JSON.stringify(data.listRadiologyDomainProgresses.items));

    if (data.listRadiologyDomainProgresses.items.length > 0) {
      console.log(`Found existing progress for Radiology domain ${textDomainKey}:`, data.listRadiologyDomainProgresses.items[0].progress);
      return data.listRadiologyDomainProgresses.items[0];
    }
    
    console.log(`No progress found for Radiology domain ${textDomainKey}`);
    return null;
  } catch (error) {
    console.error(`Error fetching Radiology domain progress for ${domainKey}:`, error);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack,
      errors: error.errors || 'No nested errors'
    });
    return null;
  }
};
